import React, { useState } from "react";
import apiService from "../../ApiService";
import "./BillUpload.css";

const BillUpload = ({ onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);

    // Create previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews(newPreviews);
    setError(null);
    setSuccess(null);
  };

  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);

    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one image");
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Add multiple images
      if (selectedFiles.length === 1) {
        formData.append("image", selectedFiles[0]);
      } else {
        selectedFiles.forEach((file) => {
          formData.append("images[]", file);
        });
      }

      // Optional: Add user_id if available
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      if (userData.UserID) {
        formData.append("user_id", userData.UserID);
      }

      const response = await apiService.uploadBill(formData);
      setSuccess("Bill uploaded and processed successfully!");
      setSelectedFiles([]);
      setPreviews([]);

      // Call callback if provided
      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.message || "Failed to upload bill");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setPreviews([]);
    setError(null);
    setSuccess(null);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      setShowCamera(true);
      setError(null);

      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err) {
      setError(
        "Camera access denied. Please allow camera permissions or use file upload.",
      );
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(
      (blob) => {
        const timestamp = Date.now();
        const file = new File([blob], `camera-${timestamp}.jpg`, {
          type: "image/jpeg",
        });

        // Add to existing files
        const newFiles = [...selectedFiles, file];
        const newPreview = URL.createObjectURL(file);
        const newPreviews = [...previews, newPreview];

        setSelectedFiles(newFiles);
        setPreviews(newPreviews);
        setError(null);
        setSuccess(`Photo captured! Total: ${newFiles.length} image(s)`);

        // Clear success message after 2 seconds
        setTimeout(() => setSuccess(null), 2000);
      },
      "image/jpeg",
      0.95,
    );
  };

  // Cleanup camera on unmount
  React.useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return (
    <div className="bill-upload-container">
      <h2>Upload Bill</h2>

      <div className="upload-section">
        <div className="upload-options">
          <div className="file-input-wrapper">
            <input
              type="file"
              id="billImages"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="billImages" className="file-input-label">
              <i className="fas fa-cloud-upload-alt"></i>
              <span>Choose from Device</span>
            </label>
          </div>

          <div className="camera-button-wrapper">
            <button
              type="button"
              onClick={showCamera ? stopCamera : startCamera}
              className="camera-btn"
              disabled={uploading}
            >
              <i className={`fas fa-${showCamera ? "times" : "camera"}`}></i>
              <span>{showCamera ? "Close Camera" : "Take Photo"}</span>
            </button>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="selected-files-info">
            <p>{selectedFiles.length} image(s) ready to upload</p>
          </div>
        )}
      </div>

      {/* Camera View */}
      {showCamera && (
        <div className="camera-section">
          <div className="camera-container">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera-preview"
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <div className="camera-controls">
              <button
                type="button"
                onClick={capturePhoto}
                className="capture-btn"
                disabled={uploading}
              >
                <i className="fas fa-camera"></i>
                <span>Capture</span>
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="close-camera-btn"
                disabled={uploading}
              >
                <i className="fas fa-times"></i>
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Previews */}
      {previews.length > 0 && (
        <div className="preview-section">
          <h3>Preview</h3>
          <div className="preview-grid">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                <img src={preview} alt={`Preview ${index + 1}`} />
                <div className="preview-overlay">
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="remove-btn"
                    disabled={uploading}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <p className="preview-label">Image {index + 1}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="message error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="message success-message">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      {/* Action Buttons */}
      {selectedFiles.length > 0 && (
        <div className="action-buttons">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="btn-primary"
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Processing...
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i> Upload & Extract
              </>
            )}
          </button>
          <button
            onClick={handleClear}
            disabled={uploading}
            className="btn-secondary"
          >
            <i className="fas fa-times"></i> Clear
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="upload-instructions">
        <h4>Instructions:</h4>
        <ul>
          <li>Choose images from your device OR use camera to take photos</li>
          <li>You can capture multiple photos for a single bill</li>
          <li>Multiple images will be processed as a single bill</li>
          <li>
            AI will automatically extract merchant info, items, and amounts
          </li>
          <li>You can edit the extracted data after upload</li>
        </ul>
      </div>
    </div>
  );
};

export default BillUpload;
